import { Box, Text } from '@conteg/ui';
import { OneTimePasswordProps } from 'pages/storage-unit-detail/components/actions/one-time-password/one-time-password';
import ForcedOpenUnitWithReservation from 'pages/storage-unit-detail/components/actions/open-unit/forced-open-unit-with-reservation';
import OpenWithPin from 'pages/storage-unit-detail/components/actions/open-unit/open-with-pin';
import RequestMaintenance from 'pages/storage-unit-detail/components/actions/request-maintenance/request-maintenance';
import {
  OpenModal,
  OpenModalTypes,
} from 'pages/storage-unit-detail/components/actions/reservation-actions';
import { TwoFactorWarning } from 'pages/storage-unit-detail/components/actions/two-factor-warning';
import UpdateUnitStatus from 'pages/storage-unit-detail/components/actions/update-unit-status/update-unit-status';
import { useTranslation } from 'react-i18next';
import {
  StorageUnitDtoFragment,
  StorageUnitStatus,
} from 'types/generated/graphql';

import ChangeBlockingReservationTenantSubjectId from './change-blocking-reservation-tenant-subject-id/change-blocking-reservation-tenant-subject-id';
import OpenWithoutPin from './open-unit/open-without-pin';
import ResendAccessCode from './resend-access-code/resend-access-code';
import TerminateReservation from './terminate-reservation/terminate-reservation';
import UpdateReservationState from './update-reservation-state/update-reservation-state';

type ActionsModalContentProps = {
  contentType: OpenModal;
  storageUnit: StorageUnitDtoFragment;
  oneTimePassword?: OneTimePasswordProps;
  onClose?: () => void;
  onDirectClose?: () => void;
};

const ActionsModalContent = ({
  contentType,
  storageUnit,
  onClose,
  onDirectClose,
}: ActionsModalContentProps) => {
  const { t } = useTranslation();
  const tenantSubjectId = storageUnit.allocation?.reservation?.tenantSubjectId;
  const reservationId =
    storageUnit.allocation?.reservation?.id ??
    storageUnit.firstUpcomingReservation?.id;
  const customerPinCredentialId =
    storageUnit.allocation?.reservation?.customerPinCredentials?.[0]?.id;
  const trackingIdentifier =
    storageUnit.allocation?.contents?.[0]?.trackingIdentifiers?.[0]?.identifier;
  const serviceType = storageUnit.allocation?.reservation?.serviceType;
  const isDeliverySubmission =
    storageUnit.allocation?.reservation?.isDeliverySubmission;
  const storageUnitId = storageUnit.storageUnit.id;

  // The parcel is protected by a recipient release code (UPS EPRA) nobody has verified. The actions
  // below that would release it anyway are already restricted to a dedicated role, so anyone reaching
  // them may proceed - the warning is there so none of them is done by habit without noticing.
  const isTwoFactorPending =
    !!storageUnit.allocation?.reservation?.is2FaRequired &&
    !storageUnit.allocation?.reservation?.twoFactorVerifiedAt;
  const twoFactorFailedAttempts =
    storageUnit.allocation?.reservation?.twoFactorFailedAttempts ?? undefined;

  const withTwoFactorWarning = (content: JSX.Element) => (
    <>
      <TwoFactorWarning
        isTwoFactorPending={isTwoFactorPending}
        failedAttempts={twoFactorFailedAttempts}
      />
      {content}
    </>
  );

  switch (contentType) {
    case OpenModalTypes.ForcedOpenStorageUnitWithReservation:
      return withTwoFactorWarning(
        <ForcedOpenUnitWithReservation
          storageUnitId={storageUnit.storageUnit.id}
          reservationId={reservationId}
          onClose={onClose}
          onDirectClose={onDirectClose}
        />
      );
    case OpenModalTypes.OpenStorageUnit:
      return (
        <OpenWithPin
          storageUnitId={storageUnit.storageUnit.id}
          reservationId={reservationId}
          requiresTwoFactor={isTwoFactorPending}
          twoFactorFailedAttempts={
            storageUnit.allocation?.reservation?.twoFactorFailedAttempts
          }
          reservationStatus={storageUnit.allocation?.reservation?.status}
          serviceType={serviceType}
          tenantSubjectId={tenantSubjectId}
          openWithoutVerifyStep={false}
        />
      );
    case OpenModalTypes.OpenStorageUnitWithoutPin:
      return <OpenWithoutPin storageUnitId={storageUnit.storageUnit.id} />;
    case OpenModalTypes.UpdateStatus:
      return (
        <UpdateUnitStatus
          storageUnitId={storageUnit.storageUnit.id}
          status={
            storageUnit.storageUnit.storageUnitStatus ===
            StorageUnitStatus.Broken
              ? StorageUnitStatus.Usable
              : StorageUnitStatus.Broken
          }
        />
      );
    case OpenModalTypes.RequestMaintenance:
      return <RequestMaintenance storageUnitId={storageUnit.storageUnit.id} />;
    case OpenModalTypes.TerminateReservation:
      return reservationId
        ? withTwoFactorWarning(
            <TerminateReservation reservationId={reservationId} />
          )
        : null;
    case OpenModalTypes.UpdateReservationState:
      return withTwoFactorWarning(
        <UpdateReservationState
          isTwoFactorPending={isTwoFactorPending}
          reservationId={reservationId as string}
          tenantSubjectId={tenantSubjectId as string}
          customerPinCredentialId={customerPinCredentialId}
          trackingIdentifier={trackingIdentifier}
          serviceType={serviceType}
          isDeliverySubmission={isDeliverySubmission}
          storageUnitId={storageUnitId}
        />
      );
    case OpenModalTypes.ResendAccessCode:
      return <ResendAccessCode reservationId={reservationId as string} />;
    case OpenModalTypes.ChangeBlockingReservationTenantSubjectId:
      return (
        <ChangeBlockingReservationTenantSubjectId
          reservationId={reservationId as string}
        />
      );
  }

  if (contentType === null) return null;

  return (
    <Box textAlign="center">
      <Text
        testId="modal-content-error"
        variant="highlight"
        content={t('Page.StorageUnit.Actions.ModalError')}
      />
    </Box>
  );
};

export default ActionsModalContent;
